import { routes } from '../../router/router.tsx';
import { buildMomentRoute, createPathType, MomentRoute } from './types.ts';
import { FeedLayout } from '../layouts/MainFeed/FeedLayout.tsx';
import { generateFetchFeedQuery } from '../net/feed-fetch-query.ts';
import { TesterPage } from '../bridge/bridge-tester.tsx';
import { FeedQueryResult, LookbookAPIResponse } from '../net/softbox-types.ts';
import { feedConfigurationPages } from '../layouts/LayoutConfiguration/layout-configuration-factory.ts';
import DoNotSell from '../layouts/DoNotSell/DoNotSell.tsx';
import ManagePrivacy from '../layouts/ManagePrivacy/ManagePrivacy.tsx';
import TermsOfUse from '../layouts/TermsOfUse/TermsOfUse.tsx';
import DataRequestForm from '../layouts/DataRequestForm/DataRequestForm.tsx';
import OptInLayout from '../layouts/OptIn/OptIn.tsx';
import { GalleryRouteParams, generateFetchLookbookQuery, } from '@moment/net/gallery-fetch-query.ts';
import { GalleryLayout } from '@moment/components/Gallery/layout/GalleryLayout.tsx';
import {
  generateFetchOssVersionQuery,
  OssVersionFetchResponse,
  VersionRouteParams,
} from '@moment/net/version-fetch-query.ts';
import { AndroidOssLicensesLayout } from '@moment/layouts/AndroidOssLicenses/AndroidOssLicenses.tsx';
import { WithCommonPageData } from '@pages/Root/ContextWrapper/WithInterest/WithCommonPageData.tsx';
import { CONTENT_NOT_AVAILABLE_PATH, LOOKBOOK_ROUTE_PATH, } from '@pages/Root/router/constants.ts';
import { HoroscopesLayout } from '@/moment/layouts/Horoscopes/HoroscopesLayout.tsx';
import { genFetchHoroscopesQuery } from '../net/classic-fotoscapes/fetch-horoscopes.ts';
import { TRIPOD_LIVE_PREVIEW } from '@moment/routes/tripod-router.tsx';
import FeedbackForm from '@moment/layouts/FeedbackForm/FeedbackForm.tsx';
import { TabContainerLayout } from '@moment/layouts/TabContainerLayout/TabContainerLayout.tsx';
import ContentNotAvailable from '@moment/layouts/ContentNotAvailable/ContentNotAvailable.tsx';
import NotFound from '../layouts/NotFound/NotFound.tsx';

const feedRoutes = feedConfigurationPages();

const feedMomentRoutes = feedRoutes.map((feedRoute) => {
  const { schedule, path, title, description, sourceLink } = feedRoute;
  const output: MomentRoute<Record<string, string>, FeedQueryResult> = {
    path: createPathType<Record<string, string>>(path),
    title,
    description,
    sourceLink,
    element: (
      <WithCommonPageData>
        <FeedLayout
          queryFn={generateFetchFeedQuery(schedule)}
          schedule={schedule}
          configuration={feedRoute}
        />
      </WithCommonPageData>
    ),
    prefetchQuery: generateFetchFeedQuery(schedule),
    feedRoute,
  };
  return buildMomentRoute(output);
});

export const LOOKBOOK_ROUTE: MomentRoute<GalleryRouteParams, LookbookAPIResponse> = {
  path: createPathType<GalleryRouteParams>(LOOKBOOK_ROUTE_PATH),
  title: 'Moment | :lookbook',
  description: 'Moment',
  element: (
    <WithCommonPageData>
      <GalleryLayout queryFn={generateFetchLookbookQuery} />
    </WithCommonPageData>
  ), //queryFn injected in buildMomentRoute
  prefetchQuery: generateFetchLookbookQuery, //params are injected in server-entrypoint & in gallerylayout
  prefetchQueryKeyMutator: (params) => {
    if (params.lookbookId) {
      return ['lookbook', params.lookbookId];
    } else {
      return [params]
    }
  },
};

export const HOROSCOPES_ROUTE: MomentRoute<Record<string, string>, void> = {
  path: createPathType<Record<string, string>>('horoscopes'),
  title: 'Moment | Horoscopes',
  description: 'Moment | Horoscopes',
  element: (
    <WithCommonPageData>
      <HoroscopesLayout />
    </WithCommonPageData>
  ),
  prefetchQuerys: genFetchHoroscopesQuery,
};

export const BRIDGE_TEST_ROUTE: MomentRoute<Record<string, string>, void> = {
  path: createPathType<Record<string, string>>('/bridge-tester-page'),
  title: 'Bridge Tester',
  description: 'Moment',
  element: <TesterPage />,
  prefetchQuery: undefined,
};

export const ANDROID_OSS_ROUTE: MomentRoute<
  VersionRouteParams,
  OssVersionFetchResponse
> = {
  path: createPathType<Record<string, string>>('/android-oss/:version'),
  title: 'OSS Licenses',
  description: 'Android OSS Licenses',
  element: <AndroidOssLicensesLayout />,
  prefetchQuery: generateFetchOssVersionQuery,
};

export const OPT_IN_ROUTE: MomentRoute<Record<string, string>, void> = {
  path: createPathType<Record<string, string>>('moment-opt-in'),
  title: 'Moment',
  description: 'Moment',
  element: <OptInLayout />,
  prefetchQuery: undefined,
};

export const DO_NOT_SELL_ROUTE: MomentRoute<Record<string, string>, void> = {
  path: createPathType<Record<string, string>>('do-not-sell'),
  title: 'Moment | Do not sell my information',
  description: 'Moment | Do not sell my information',
  element: <DoNotSell />,
  prefetchQuery: undefined,
};

export const MANAGE_PRIVACY_ROUTE: MomentRoute<Record<string, string>, void> = {
  path: createPathType<Record<string, string>>('manage-privacy'),
  title: 'Moment | Manage Privacy',
  description: 'Moment | Manage Privacy',
  element: <ManagePrivacy />,
};

export const TERMS_OF_USE_ROUTE: MomentRoute<Record<string, string>, void> = {
  path: createPathType<Record<string, string>>('terms-of-use'),
  title: 'Moment | Terms of Use',
  description: 'Moment | Terms of Use',
  element: <TermsOfUse />,
};

export const DELETE_DATA_REQ_ROUTE: MomentRoute<
  Record<string, string>,
  void
> = {
  path: createPathType<Record<string, string>>(
    'manage-privacy-delete-data-req'
  ),
  title: 'Moment | Manage Privacy - Request to delete my data',
  description: 'Moment | Manage Privacy  - Request to delete my data',
  element: <DataRequestForm mode={'delete'} />,
};

export const GIVE_DATA_REQ_ROUTE: MomentRoute<Record<string, string>, void> = {
  path: createPathType<Record<string, string>>('manage-privacy-give-data-req'),
  title: 'Moment | Manage Privacy - Request to give my data',
  description: 'Moment | Manage Privacy  - Request to give my data',
  element: <DataRequestForm mode={'give'} />,
};

export const FEEDBACK_ROUTE: MomentRoute<Record<string, string>, void> = {
  path: createPathType<Record<string, string>>('provide-feedback'),
  title: 'Moment | Provide feedback',
  description: 'Moment | Provide feedback',
  element: <FeedbackForm />,
};

export const NOT_FOUND_ROUTE: MomentRoute<Record<string, string>, void> = {
  path: createPathType<Record<string, string>>('not-found'),
  title: 'Moment | Not Found',
  description: 'Moment | Not Found',
  element: <NotFound />,
};

const CONTENT_NOT_AVAILABLE_ROUTE: MomentRoute<Record<string, string>, void> = {
  path: createPathType<Record<string, string>>(CONTENT_NOT_AVAILABLE_PATH),
  title:
    'Content Not Available | Curated Stories and Stunning Photography | Fotoscapes',
  description: `Unfortunately, we can't bring you your favorite content in this cuntry.  We'll bring it back to you when you're back in an area we can service.`,
  element: <ContentNotAvailable />,
};

export const exclusiveMomentRoutes = [...feedMomentRoutes];

export const CONTENT_NOT_AVAILABLE_MOMENT_ROUTE = buildMomentRoute(
  CONTENT_NOT_AVAILABLE_ROUTE
);

export const momentRoutes = [
  ...feedMomentRoutes,
  buildMomentRoute(LOOKBOOK_ROUTE),
  buildMomentRoute(HOROSCOPES_ROUTE),
  buildMomentRoute(BRIDGE_TEST_ROUTE),
  buildMomentRoute(DO_NOT_SELL_ROUTE),
  buildMomentRoute(MANAGE_PRIVACY_ROUTE),
  buildMomentRoute(TERMS_OF_USE_ROUTE),
  buildMomentRoute(DELETE_DATA_REQ_ROUTE),
  buildMomentRoute(GIVE_DATA_REQ_ROUTE),
  buildMomentRoute(OPT_IN_ROUTE),
  buildMomentRoute(ANDROID_OSS_ROUTE),
  buildMomentRoute(TRIPOD_LIVE_PREVIEW),
  buildMomentRoute(FEEDBACK_ROUTE),
  buildMomentRoute(NOT_FOUND_ROUTE),
  CONTENT_NOT_AVAILABLE_MOMENT_ROUTE,
  ...routes,
  // The new Tab Container route. It must be placed after specific routes but before any final catch-all ('*').
  // The '/*' is a wildcard to match nested page routes within the container.
  {
    path: '/tc/:containerName/*',
    element: <TabContainerLayout />,
  },
];
